import { useCallback, useEffect, useRef } from "react";
import { useProctoringApiMutation } from "@assessment/store/slices/api/assessmentApiSlice";
import { differentiateToasts } from "@assessment/utils/toast/differentiateToasts";

type BrowserViolationType =
  | "refreshedPage"
  | "fullscreenShift"
  | "tabShift"
  | "clickedOutsideAssessmentScreen"
  | "functionalKeys"
  | "altTab"
  | "copy"
  | "cut"
  | "paste"
  | "brv3";

interface BrowserViolationConfig {
  cooldownMs: number;
  showWarningModal: boolean;
  toastMessageKey?: string;
}

const BROWSER_VIOLATION_CONFIG: Readonly<
  Record<BrowserViolationType, BrowserViolationConfig>
> = {
  refreshedPage: {
    cooldownMs: 1000,
    showWarningModal: true
  },
  fullscreenShift: {
    cooldownMs: 400,
    showWarningModal: true
  },
  tabShift: {
    cooldownMs: 1000,
    showWarningModal: true
  },
  clickedOutsideAssessmentScreen: {
    cooldownMs: 1000,
    showWarningModal: true
  },
  functionalKeys: {
    cooldownMs: 100,
    showWarningModal: true
  },
  altTab: {
    cooldownMs: 100,
    showWarningModal: true
  },
  copy: {
    cooldownMs: 300,
    showWarningModal: false,
    toastMessageKey: "assessment.toast.messages.copyNotAllowed"
  },
  cut: {
    cooldownMs: 300,
    showWarningModal: false,
    toastMessageKey: "assessment.toast.messages.cutRestricted"
  },
  paste: {
    cooldownMs: 300,
    showWarningModal: false,
    toastMessageKey: "assessment.toast.messages.pasteNotAllowed"
  },
  brv3: {
    cooldownMs: 300,
    showWarningModal: false,
    toastMessageKey: "assessment.toast.messages.contextMenuNotAllowed"
  }
};

const FUNCTION_KEY_REGEX = /^F\d{1,2}$/;
const ALT_TAB_KEY = "Tab";
type ListenerCleanup = () => void;

interface UseBrowserProctoringOptions {
  assessmentRef: React.RefObject<HTMLDivElement | null>;
  browserProctoring: boolean;
  assessmentHasStarted: boolean;
  isCodingQues: boolean;
  ongoingId?: string;
  scheduleId?: string;
  onRequireWarningModal: () => void;
}

interface UseBrowserProctoringReturn {
  stopBrowserProctoring: () => void;
}

export const useBrowserProctoring = ({
  assessmentRef,
  browserProctoring,
  assessmentHasStarted,
  isCodingQues,
  ongoingId,
  scheduleId,
  onRequireWarningModal
}: UseBrowserProctoringOptions): UseBrowserProctoringReturn => {
  const [proctoringApi] = useProctoringApiMutation();
  const violationCooldownRef = useRef<
    Partial<Record<BrowserViolationType, number>>
  >({});
  const listenerCleanupRef = useRef<ListenerCleanup[]>([]);
  const listenersAttachedRef = useRef(false);
  const onRequireWarningModalRef = useRef(onRequireWarningModal);
  const runtimeStateRef = useRef({
    browserProctoring,
    assessmentHasStarted,
    ongoingId,
    scheduleId
  });

  useEffect(() => {
    runtimeStateRef.current = {
      browserProctoring,
      assessmentHasStarted,
      ongoingId,
      scheduleId
    };
  }, [browserProctoring, assessmentHasStarted, ongoingId, scheduleId]);

  useEffect(() => {
    onRequireWarningModalRef.current = onRequireWarningModal;
  }, [onRequireWarningModal]);

  const isProctoringActive = useCallback(() => {
    const state = runtimeStateRef.current;
    return state.browserProctoring && state.assessmentHasStarted;
  }, []);

  const callViolationsApi = useCallback(
    async (reason: BrowserViolationType) => {
      const { ongoingId: activeOngoingId, scheduleId: activeScheduleId } =
        runtimeStateRef.current;
      if (!activeOngoingId || !activeScheduleId) return;
      await proctoringApi({
        ongoingId: activeOngoingId,
        scheduleId: activeScheduleId,
        violationType: reason,
        body: { rawData: {} }
      });
    },
    [proctoringApi]
  );

  const emitBrowserViolation = useCallback(
    (
      violationType: BrowserViolationType,
      options?: {
        cooldownMs?: number;
        showWarningModal?: boolean;
      }
    ) => {
      if (!isProctoringActive()) return;

      const violationConfig = {
        ...BROWSER_VIOLATION_CONFIG[violationType],
        ...options
      };
      const now = Date.now();
      const lastTriggeredAt = violationCooldownRef.current[violationType] ?? 0;
      if (
        violationConfig.cooldownMs > 0 &&
        now - lastTriggeredAt < violationConfig.cooldownMs
      ) {
        return;
      }

      violationCooldownRef.current[violationType] = now;
      if (violationConfig.showWarningModal) {
        onRequireWarningModalRef.current();
      }
      if (violationConfig.toastMessageKey) {
        differentiateToasts(violationConfig.toastMessageKey);
      }
      void callViolationsApi(violationType);
    },
    [callViolationsApi, isProctoringActive]
  );

  const handleBeforeUnload = useCallback(
    (event: BeforeUnloadEvent) => {
      if (!isProctoringActive()) return;
      event.preventDefault();
      event.returnValue = "";
      emitBrowserViolation("refreshedPage");
    },
    [emitBrowserViolation, isProctoringActive]
  );

  const handleFullScreenChange = useCallback(() => {
    if (!isProctoringActive()) return;
    if (!document.fullscreenElement) {
      emitBrowserViolation("fullscreenShift");
    }
  }, [emitBrowserViolation, isProctoringActive]);

  const handleVisibilityChange = useCallback(() => {
    if (!isProctoringActive()) return;
    if (document.hidden) {
      emitBrowserViolation("tabShift");
    }
  }, [emitBrowserViolation, isProctoringActive]);

  const handleBlur = useCallback(
    (event: FocusEvent) => {
      if (!isProctoringActive()) return;
      event.preventDefault();
      const activeEl = document.activeElement;
      if (activeEl && activeEl.tagName === "IFRAME") return;
      emitBrowserViolation("clickedOutsideAssessmentScreen");
    },
    [emitBrowserViolation, isProctoringActive]
  );

  const handleKeydown = useCallback(
    (event: KeyboardEvent) => {
      if (!isProctoringActive()) return;
      if (FUNCTION_KEY_REGEX.test(event.key)) {
        emitBrowserViolation("functionalKeys");
      } else if (event.key === ALT_TAB_KEY && event.altKey) {
        emitBrowserViolation("altTab");
      }
    },
    [emitBrowserViolation, isProctoringActive]
  );

  const handleCopy = useCallback(
    (event: ClipboardEvent) => {
      if (!isProctoringActive()) return;
      event.preventDefault();
      emitBrowserViolation("copy");
    },
    [emitBrowserViolation, isProctoringActive]
  );

  const handleCut = useCallback(
    (event: ClipboardEvent) => {
      if (!isProctoringActive()) return;
      event.preventDefault();
      emitBrowserViolation("cut");
    },
    [emitBrowserViolation, isProctoringActive]
  );

  const handlePaste = useCallback(
    (event: ClipboardEvent) => {
      if (!isProctoringActive()) return;
      event.preventDefault();
      emitBrowserViolation("paste");
    },
    [emitBrowserViolation, isProctoringActive]
  );

  const handleContextMenu = useCallback(
    (event: MouseEvent) => {
      if (!isProctoringActive()) return;
      event.preventDefault();
      emitBrowserViolation("brv3");
    },
    [emitBrowserViolation, isProctoringActive]
  );

  const detachBrowserProctoringListeners = useCallback(() => {
    listenerCleanupRef.current.forEach((cleanup) => cleanup());
    listenerCleanupRef.current = [];
    listenersAttachedRef.current = false;
  }, []);

  const attachBrowserProctoringListeners = useCallback(() => {
    if (listenersAttachedRef.current) return;
    const listenerCleanups: ListenerCleanup[] = [];

    const registerWindowListener = <K extends keyof WindowEventMap>(
      eventName: K,
      listener: (event: WindowEventMap[K]) => void
    ) => {
      window.addEventListener(eventName, listener);
      listenerCleanups.push(() => {
        window.removeEventListener(eventName, listener);
      });
    };

    const registerDocumentListener = <K extends keyof DocumentEventMap>(
      eventName: K,
      listener: (event: DocumentEventMap[K]) => void
    ) => {
      document.addEventListener(eventName, listener);
      listenerCleanups.push(() => {
        document.removeEventListener(eventName, listener);
      });
    };

    const registerAssessmentNodeListener = <
      K extends keyof HTMLElementEventMap
    >(
      node: HTMLDivElement,
      eventName: K,
      listener: (event: HTMLElementEventMap[K]) => void
    ) => {
      node.addEventListener(eventName, listener);
      listenerCleanups.push(() => {
        node.removeEventListener(eventName, listener);
      });
    };

    registerDocumentListener("visibilitychange", handleVisibilityChange);
    registerDocumentListener("fullscreenchange", handleFullScreenChange);
    registerWindowListener("beforeunload", handleBeforeUnload);
    registerWindowListener("blur", handleBlur);
    registerWindowListener("keydown", handleKeydown);

    const node = assessmentRef.current;
    if (!node) {
      listenerCleanupRef.current = listenerCleanups;
      listenersAttachedRef.current = true;
      return;
    }

    registerAssessmentNodeListener(node, "contextmenu", handleContextMenu);
    if (!isCodingQues) {
      registerAssessmentNodeListener(node, "copy", handleCopy);
      registerAssessmentNodeListener(node, "paste", handlePaste);
      registerAssessmentNodeListener(node, "cut", handleCut);
    }

    listenerCleanupRef.current = listenerCleanups;
    listenersAttachedRef.current = true;
  }, [
    assessmentRef,
    handleVisibilityChange,
    handleFullScreenChange,
    handleBeforeUnload,
    handleBlur,
    handleKeydown,
    handleCopy,
    handlePaste,
    handleCut,
    handleContextMenu,
    isCodingQues
  ]);

  const stopBrowserProctoring = useCallback(() => {
    detachBrowserProctoringListeners();
    violationCooldownRef.current = {};
  }, [detachBrowserProctoringListeners]);

  useEffect(() => {
    const shouldEnableProctoring = browserProctoring && assessmentHasStarted;
    if (!shouldEnableProctoring) {
      stopBrowserProctoring();
      return;
    }

    // Rebind to keep node listeners aligned with coding/non-coding transitions.
    detachBrowserProctoringListeners();
    attachBrowserProctoringListeners();
    return detachBrowserProctoringListeners;
  }, [
    browserProctoring,
    assessmentHasStarted,
    isCodingQues,
    attachBrowserProctoringListeners,
    detachBrowserProctoringListeners,
    stopBrowserProctoring
  ]);

  return {
    stopBrowserProctoring
  };
};
